import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import gedcom from 'parse-gedcom';

import { 
  familySearchSignIn, 
  familySearchSignOut,
  importPersonAncestors, 
  getToken, 
  getPossibleRoots,
} from '../../../core/family-search';

import { getInterfaceMode } from '../../../core/ui'

import { 
  importGEDCOMPerson,
  parseGEDCOMPerson,
} from '../../../core/gedcom/actions';

import { getChart, batchUpdateChart } from '../../../core/chart';
import { clearAllCellsData, batchUpdateCells } from '../../../core/cells';
import { getGenerations, batchUpdateGenerations } from '../../../core/generations';

import { _Label, _Field } from '../styled-sidebar';
import { _Button } from '../../common/styled-buttons';

import { StyledScrollable, StyledMargin } from '../../common/styled-base';
import { _Flex } from '../../common/styled-flex';
import theme from '../../../styles/theme';


import TextField from 'material-ui/TextField';
import MenuItem from 'material-ui/MenuItem';

import Select from 'react-select';

import { filter, find, forEach, sortBy } from 'lodash';


const StyledHeading = styled.p`
  font-size: 12px;
  color: #797979;
`;

const StyledParagraph = styled.p`
  font-size: 12px;
  line-height: 16px;
  font-weight: normal;
  margin-bottom: 10px;
  em {
    color: ${theme.primary};
    font-size: 1.2em;
    font-style: normal;
  }
`;

class ImportMenu extends React.Component {

  constructor() {
    super();
    this.state = {
      personId: '',
      motherId: '',
      fatherId: '',
      fsSelected: {},
      showImport: true,
      gedcom: null,
      gedcomSelected: null,
    }
  }

  loadGEDCOM(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = e => {
      const json = gedcom.parse(e.target.result);
      const d3 = gedcom.d3ize(json);
      const person = d3.nodes[0];
      const persons = filter(d3.nodes, n => n.tag === 'INDI').map(parseGEDCOMPerson);
      const links = filter(d3.nodes, n => n.tag === 'FAM');

      this.setState({
        gedcom: {persons, links},
        gedcomSelected: persons[0],
      });
      this.props.importGEDCOMPerson(persons[0], persons, links)
    }
    
    reader.readAsText(file);
  }

  loadJSON(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = e => {
      const json = JSON.parse(e.target.result);
      this.props.batchUpdateChart(json.chart);
      this.props.batchUpdateGenerations(json.generations.generations);
      this.props.batchUpdateCells(json.cells.cells);
    }
    reader.readAsText(file);
  }

  renderFamilySearch() {
    const { 
      possibleRoots = [], 
      importPersonAncestors,
      showFirstGeneration,
      generations,
    } = this.props;

    const { personId, motherId, fatherId, fsSelected } = this.state;

    return (
      <div>
        <StyledParagraph>We will attempt to import <em>{generations} generations</em>. If you want to import more, first go to the <strong>Chart Tab</strong> and modify the number of generations.</StyledParagraph>

        <StyledParagraph>Choose one of the following:</StyledParagraph>
        <StyledHeading>Option A) Select first generation</StyledHeading>

        <Select
          name="form-field-name"
          value={{value: fsSelected, label: fsSelected.name}}
          options={possibleRoots.map(person => ({label: person.name, value: person}))}
          clearable={false}
          matchProp="label"
          onChange={({value}) => this.setState({fsSelected: value, showImport: true})}
          />
        
        <StyledHeading>Option B) Provide a Person ID</StyledHeading>
        <div>
          <TextField 
            id="name" 
            value={personId}
            hintText="xxxx-xxx"
            hintStyle={{color: 'rgba(255,255,255,0.7)'}}
            inputStyle={{color: 'rgba(255,255,255,0.8'}}
            underlineFocusStyle={{borderColor: theme.primary}}
            onChange={(e, value) => this.setState({personId: value, showImport: true})} 
            />
        </div>

        <StyledHeading>Option C) Provide Father and Mother ID's</StyledHeading>
        <div>
          <TextField 
            id="father" 
            value={fatherId}
            hintText="xxxx-xxx (Father)"
            hintStyle={{color: 'rgba(255,255,255,0.7)'}}
            inputStyle={{color: 'rgba(255,255,255,0.8'}}
            underlineFocusStyle={{borderColor: theme.primary}}
            onChange={(e, value) => this.setState({fatherId: value, showImport: true})} 
            />
        </div>

        <div>
          <TextField 
            id="mother" 
            value={motherId}
            hintText="xxxx-xxx (Mother)"
            hintStyle={{color: 'rgba(255,255,255,0.7)'}}
            inputStyle={{color: 'rgba(255,255,255,0.8'}}
            underlineFocusStyle={{borderColor: theme.primary}}
            onChange={(e, value) => this.setState({motherId: value, showImport: true})} 
            />
        </div>

        {!this.state.showImport 
          ? <StyledParagraph>*If it appears that not all of your data has imported, please verify that it exists in <a href="https://familysearch.org" target="_blank">FamilySearch</a>. Due to privacy concerns you do not have access to living ancestors' geneologies unless you manually add them. See <a href="https://familysearch.org/blog/en/common-mistakes-family-tree-data/" target="_blank">common problems</a> for more help.</StyledParagraph> 
          : <_Button block onClick={() => {
                this.setState({showImport: false});
                if(motherId !== '' || fatherId !== '') {
                  importPersonAncestors({
                    father: { personId: fatherId, unknown: !fatherId },
                    mother: { personId: motherId, unknown: !motherId },
                  }, generations, showFirstGeneration);
                } else if(personId !== '') {
                  importPersonAncestors({personId}, generations, showFirstGeneration);
                } else {
                  importPersonAncestors(fsSelected, generations, showFirstGeneration);
                }
              }}>
              Import Ancestors
            </_Button>
        }
      </div>
    )
  }

  renderGEDCOM() {
    const { gedcomSelected, gedcom } = this.state;

    const selected = gedcomSelected;
    const persons = sortBy(gedcom.persons, p => p.name);
    return ( 
      <div>
        <StyledHeading>Select first generation</StyledHeading>
        <Select
          name="form-field-name"
          value={{value: selected, label: `${selected.name} ${selected.birth || '?'}-${selected.death || '?'}`}}
          options={persons.map(p => ({label: `${p.name} ${p.birth || '?'}-${p.death || '?'}`, value: p}))}
          clearable={false}
          matchProp="label"
          onChange={({value}) => {
            if(value) {
              this.props.importGEDCOMPerson(value, persons, gedcom.links);
              this.setState({gedcomSelected: value});
            }
          }}
          />
      </div>
    )
  }

  renderContent() {
    const { familySearchSignIn, isAuthenticated, interfaceMode } = this.props;

    if(this.state.gedcom) {
      return this.renderGEDCOM();
    } else if(isAuthenticated) {
      return this.renderFamilySearch();
    }
    
    return (
      <div>
        <_Button block onClick={familySearchSignIn} className="mb2">
          Sign in to FamilySearch
        </_Button>

        <label>
          <_Button block className="mb2">
            Import GEDCOM file
            <input type="file" style={{position: 'fixed', top: '-100em'}} onChange={e => this.loadGEDCOM(e)} />
          </_Button>
        </label>

        {(interfaceMode === 'prince' || interfaceMode === 'json') && 
          <label>
            <_Button block className="mb2">
              Import JSON file
              <input type="file" style={{position: 'fixed', top: '-100em'}} onChange={e => this.loadJSON(e)} />
            </_Button>
          </label>
        }
      </div>
    )    

  }

  render() {
    const { clearAllCellsData, isAuthenticated, familySearchSignOut } = this.props;

    return (
      <StyledScrollable padding='15px'>
        <_Flex>
          {this.renderContent()}
        </_Flex>
        {isAuthenticated &&
          <_Button onClick={familySearchSignOut} className="mb2">Sign Out</_Button>
        }
        <_Button block onClick={() => { clearAllCellsData(); this.setState({showImport: true, gedcom: null})}}>
          Clear All Cell Data
        </_Button>
      </StyledScrollable>
    )
  }
}

const mapStateToProps = createSelector(
  getPossibleRoots,
  getToken,
  getChart,
  getGenerations,
  getInterfaceMode,
  (possibleRoots, token, chart, generations, interfaceMode) => ({
    generations: chart.generations,
    isAuthenticated: !!token,
    showFirstGeneration: generations[0].thickness > 0,
    possibleRoots,
    interfaceMode,
  })
)

const mapDispatchToProps = {
  familySearchSignIn,
  familySearchSignOut, 
  importPersonAncestors, 
  clearAllCellsData,
  importGEDCOMPerson,  
  batchUpdateChart,
  batchUpdateGenerations,
  batchUpdateCells,
}
export default connect(mapStateToProps, mapDispatchToProps)(ImportMenu);


// WEBPACK FOOTER //
// ./src/components/menu/import-menu/index.js